import { useState, useRef, useEffect, useCallback } from 'react';
import { useHistory } from "react-router";
import { enumCategorieRecherche } from '../enums/enumCategorieRecherche.js';
import OffreEmploiService from '../services/offreEmploiService.js';
import RechercheService from '../services/rechercheService.js';
import Helper from '../utils/helper.js';

export const useOffersSearch = (currentUser, adminImpersonatingEtudiantId) => {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [offresEmploi, setOffresEmploi] = useState([]);
    const [error, setError] = useState(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const urlParams = new URLSearchParams(window.location.search);
    const search = parseInt(urlParams.get('page'));
    const favQuery = urlParams.get('fav');
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(favQuery === '1' || favQuery === 'true');

    const previousSearchRef = useRef(null);
    const requestIdRef = useRef(0);
    const favZeroBasedTriedRef = useRef(false);
    const debounceTimerRef = useRef(null);

    const offreEmploiService = new OffreEmploiService();
    const rechercheService = new RechercheService();

    const itemsPerPage = 12;
    const firstItemIndex = (search && !isNaN(search)) ? (itemsPerPage * (search - 1)) : 0;

    const [lazyParams, setLazyParams] = useState({
        first: firstItemIndex,
        rows: itemsPerPage,
        page: search && !isNaN(search) ? parseInt(search) : 1,
        sortField: 'dateCreation',
        filters: []
    });

    const loadLazyData = useCallback(() => {
        // debounce to group rapid filter changes
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        // show loading immediately for UX
        setLoading(true);
        setError(null);

        debounceTimerRef.current = setTimeout(() => {
            const sp = new URLSearchParams(window.location.search);
            sp.set('page', lazyParams.page);
            sp.set('fav', showFavoritesOnly ? '1' : '0');
            window.history.pushState({}, '', window.location.pathname + '?' + sp.toString());

            const currentId = ++requestIdRef.current;

            const httpPromise = showFavoritesOnly
                ? offreEmploiService.getMatchingOffersOnFavorite(lazyParams, adminImpersonatingEtudiantId)
                : rechercheService.getRechercheAffineeOffreEmploiPaginated(lazyParams, adminImpersonatingEtudiantId);

            const handleSuccess = (res, responseId) => {
                if (responseId !== requestIdRef.current) return;

                let items = [];
                let count = 0;
                if (Array.isArray(res)) {
                    items = res;
                    count = res.length;
                } else if (res) {
                    items = res.items || res.data || res.results || res.result || res.offres || res.offresEmploi || [];
                    const tc = res.totalCount ?? res.total ?? res.count;
                    count = typeof tc === 'number' ? tc : (Array.isArray(items) ? items.length : 0);
                }

                items = Array.isArray(items) ? items : [];

                if (showFavoritesOnly) {
                    if (items.length === 0 && lazyParams.page > 1) {
                        setLazyParams(prev => ({ ...prev, first: 0, page: 1 }));
                        setLoading(false);
                        return;
                    }
                    if (items.length === 0 && lazyParams.page === 1 && !favZeroBasedTriedRef.current) {
                        favZeroBasedTriedRef.current = true;
                        const fallbackId = ++requestIdRef.current;
                        offreEmploiService.getMatchingOffersOnFavorite({ ...lazyParams, page: 0 }, adminImpersonatingEtudiantId)
                            .then(r2 => {
                                if (fallbackId !== requestIdRef.current) return;
                                handleSuccess(r2);
                            })
                            .catch(err => {
                                if (fallbackId !== requestIdRef.current) return;
                                setError(err?.message || 'failed');
                                setLoading(false);
                            });
                        return;
                    }
                }

                items.forEach(offer => {
                    if (offer?.dateCreation) {
                        offer.dateCreation = Helper.dateTimeToLocaleDateTime(offer.dateCreation)
                    }
                });

                setTotalCount(count);
                setOffresEmploi(items);
                setLoading(false);
            };

            httpPromise
                .then(res => handleSuccess(res, currentId))
                .catch((err) => {
                    if (currentId !== requestIdRef.current) return;
                    setError(err?.message || 'failed');
                    setLoading(false);
                });
        }, 500);
    }, [lazyParams, showFavoritesOnly, adminImpersonatingEtudiantId]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    const toggleFavoritesOnly = useCallback(() => {
        setShowFavoritesOnly(prev => {
            const next = !prev;
            if (next) {
                previousSearchRef.current = { lazyParams: { ...lazyParams } };
                setLazyParams(prevLP => ({ ...prevLP, first: 0, page: 1, filters: [] }));
            } else {
                if (previousSearchRef.current?.lazyParams) {
                    setLazyParams({ ...previousSearchRef.current.lazyParams, ...previousSearchRef.current?.lazyParams.first, ...previousSearchRef.current?.lazyParams.page});
                } else {
                    setLazyParams(prevLP => ({ ...prevLP, first: 0, page: 1 }));
                }
            }
            return next;
        });
    }, [lazyParams]);

    useEffect(() => {
        favZeroBasedTriedRef.current = false;
    }, [showFavoritesOnly]);

    return {
        loading,
        totalCount,
        setTotalCount,
        offresEmploi,
        setOffresEmploi,
        error,
        initialLoadComplete,
        setInitialLoadComplete,
        showFavoritesOnly,
        toggleFavoritesOnly,
        lazyParams,
        setLazyParams,
        loadLazyData
    };
};
